export const YOUTUBE_ORIGIN = "https://www.youtube.com";

/** YouTube's static-thumbnail host + path; the id and file name are appended. No API/network call. */
const YOUTUBE_THUMBNAIL_BASE = "https://i.ytimg.com/vi";

/** YouTube embed params. rel=0 only limits end-screen suggestions to the same channel;
 *  enablejsapi=1 lets the modal hear the player's "ended" message. */
const YOUTUBE_EMBED_PARAMS: Readonly<Record<string, string>> = {
  rel: "0",
  modestbranding: "1",
  showinfo: "0",
  iv_load_policy: "3",
  playsinline: "1",
  enablejsapi: "1",
};

/** Vimeo player params (title/byline/portrait work unless the video owner's embed settings lock them). */
const VIMEO_EMBED_PARAMS: Readonly<Record<string, string>> = {
  title: "0",
  byline: "0",
  portrait: "0",
  badge: "0",
  dnt: "1",
};

/**
 * The YouTube video id of a pasted link (watch?v=ID | youtu.be/ID | shorts/ID | embed/ID), or
 * undefined for empty input or anything that is not a YouTube link.
 */
export function youTubeVideoId(url?: string | null): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;
  return trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/,
  )?.[1];
}

/** The static hqdefault thumbnail of a YouTube link, or undefined when it is not a YouTube link. */
export function youTubeThumbnailUrl(url?: string | null): string | undefined {
  const id = youTubeVideoId(url);
  return id ? `${YOUTUBE_THUMBNAIL_BASE}/${id}/hqdefault.jpg` : undefined;
}

/**
 * Turn a pasted video link into an EMBEDDABLE player URL for an <iframe>.
 *
 * Senders paste a normal YouTube/Vimeo *watch* link (the Landing tab's "Paste a YouTube or
 * Vimeo link" field). A watch URL in an iframe renders the site's full page (or is refused by
 * X-Frame-Options) instead of an inline player — so it must be rewritten to the embed form:
 *   youtube.com/watch?v=ID | youtu.be/ID | youtube.com/shorts/ID | youtube.com/embed/ID
 *     → youtube.com/embed/ID?rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&playsinline=1&enablejsapi=1
 *       (a pasted embed URL keeps its own other params, e.g. si / start)
 *   vimeo.com/ID | vimeo.com/video/ID | vimeo.com/ID/HASH | vimeo.com/ID?h=HASH | player.vimeo.com/video/ID[?h=HASH]
 *     → player.vimeo.com/video/ID?[h=HASH&]title=0&byline=0&portrait=0&badge=0&dnt=1
 *       (HASH is the privacy token of an unlisted video; without it Vimeo refuses to play;
 *       a pasted player URL keeps its own other params and #fragment)
 * An unrecognized host is returned as-is (best effort — it may already be a direct embeddable
 * URL). Returns undefined for empty input.
 * Platform limits: YouTube ignores showinfo/modestbranding now, and rel=0 only limits the end
 * screen to the same channel's videos — which is why the modal closes when the video ends.
 */
export function toVideoEmbedUrl(url?: string | null): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;

  const ytId = youTubeVideoId(trimmed);
  if (ytId) {
    const embed = new URL(`${YOUTUBE_ORIGIN}/embed/${ytId}`);
    // A pasted embed URL keeps its own params (si, start, …); the clean-player keys are set once.
    if (/youtube\.com\/embed\//.test(trimmed)) {
      parseUrl(trimmed)?.searchParams.forEach((value, key) => {
        embed.searchParams.set(key, value);
      });
    }
    for (const [key, value] of Object.entries(YOUTUBE_EMBED_PARAMS))
      embed.searchParams.set(key, value);
    return embed.toString();
  }

  const vimeo = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([A-Za-z0-9]+))?/);
  if (vimeo?.[1]) {
    const parsed = parseUrl(trimmed);
    const hash = vimeo[2] ?? parsed?.searchParams.get("h");
    const embed = new URL(`https://player.vimeo.com/video/${vimeo[1]}`);
    // A pasted player URL keeps its own params (autoplay, …) and #fragment (#t=30s).
    if (/player\.vimeo\.com\/video\//.test(trimmed) && parsed) {
      parsed.searchParams.forEach((value, key) => {
        embed.searchParams.set(key, value);
      });
      embed.hash = parsed.hash;
    }
    if (hash) embed.searchParams.set("h", hash);
    for (const [key, value] of Object.entries(VIMEO_EMBED_PARAMS))
      embed.searchParams.set(key, value);
    return embed.toString();
  }

  return trimmed;
}

function parseUrl(value: string): URL | undefined {
  try {
    return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return undefined;
  }
}

/** True when an embed URL is a YouTube player (the only one that posts state messages). */
export function isYouTubeEmbedUrl(url?: string | null): boolean {
  const parsed = url ? parseUrl(url.trim()) : undefined;
  return parsed?.origin === YOUTUBE_ORIGIN && parsed.pathname.startsWith("/embed/");
}

/**
 * True when a window "message" payload (usually a JSON string) is the YouTube player's "ended"
 * signal: {event:"onStateChange", info:0} or {event:"infoDelivery", info:{playerState:0}}.
 * Never throws on non-JSON / non-object data.
 */
export function isYouTubeEndedMessage(data: unknown): boolean {
  let payload: unknown = data;
  if (typeof data === "string") {
    try {
      payload = JSON.parse(data);
    } catch {
      return false;
    }
  }
  if (typeof payload !== "object" || payload === null) return false;
  const { event, info } = payload as { event?: unknown; info?: unknown };
  if (event === "onStateChange") return info === 0;
  if (event === "infoDelivery" && typeof info === "object" && info !== null) {
    return (info as { playerState?: unknown }).playerState === 0;
  }
  return false;
}

/**
 * True for a direct video-FILE URL (…/foo.mp4, .webm, .mov, .ogg, .m4v — optionally followed by a
 * `?query` or `#hash`). These play in a native <video>; a YouTube/Vimeo page needs an <iframe>
 * embed instead (see toVideoEmbedUrl). Empty input is not a file.
 */
export function isDirectVideoFile(url?: string | null): boolean {
  const trimmed = url?.trim();
  if (!trimmed) return false;
  return /\.(mp4|webm|ogg|mov|m4v)(?:[?#]|$)/i.test(trimmed);
}
